import { SimpleLineChart } from '../ui/SimpleLineChart'
import { Gauge, MultiBars, PivotTable, SeriesChart, StackedBars } from './Charts'
import { RULE_FILL, ruleColor, seriesColumns } from './display'
import { cellText, change, columnTitle, FOOT_LABELS } from './describe'
import type { Locale, ResultColumn, RunResult } from './types'

/**
 * Rezultatul, comun barei si constructorului: cifra (cu sageata fata de
 * perioada dinainte si, daca are, tinta), indicator, tabel, bare (pe una sau
 * mai multe masuri), bare stivuite, linie, suprafata, top, inel, tabel
 * incrucisat. Graficele sunt SVG scris aici si in `Charts` sau
 * `SimpleLineChart` (linia simpla) - fara biblioteca noua.
 *
 * Tabelul poarta randurile de jos ("altele", total) si culorile regulilor
 * (`display.rules`), la fel ca exportul si emailul. `onDrill(i)` = omul a
 * apasat randul grupat `i` (o bara, un punct, o celula, un rand de tabel).
 */

const DONUT_COLORS = ['#3b82c4', '#5aa36b', '#e0a43a', '#c2571f', '#7c5bb5', '#2f9e9e', '#a0362a', '#7c8896']

export function ResultView({ result, locale, onDrill }: { result: RunResult; locale: Locale; onDrill?: (row: number) => void }) {
  const measures = result.columns.filter((c) => c.role === 'measure')
  const groups = result.columns.filter((c) => c.role === 'group')
  const series = seriesColumns(result)

  if (result.rows.length === 0) {
    return <div className="od-rep-empty">{locale === 'ro' ? 'Niciun rand pentru ce ai cerut.' : 'No rows for what you asked.'}</div>
  }

  if ((result.view === 'kpi' || result.view === 'gauge') && groups.length === 0) {
    return <Kpis result={result} locale={locale} gauge={result.view === 'gauge'} />
  }

  const pick = groups.length > 0 ? onDrill : undefined
  let chart = null
  if (groups.length >= 1 && series.length >= 1) {
    const two = groups.length === 2
    if (result.view === 'pivot') chart = <PivotTable result={result} locale={locale} onPick={pick} />
    else if (result.view === 'stacked' && two) chart = <StackedBars result={result} locale={locale} onPick={pick} />
    else if (result.view === 'area' || (result.view === 'line' && (two || series.length > 1 || measures.some((m) => m.agg === 'previous')))) chart = <SeriesChart result={result} locale={locale} area={result.view === 'area'} onPick={pick} />
    else if (!two && result.view === 'line') {
      chart = (
        <SimpleLineChart
          points={result.rows.map((r) => ({ name: String(r.values[0] ?? ''), value: Number(r.values[result.columns.indexOf(series[0]!)] ?? 0) }))}
          unit={columnTitle(series[0]!, locale)}
          color="#3b82c4"
          height={220}
        />
      )
    } else if (!two && result.view === 'pie') chart = <Donut result={result} group={groups[0]!} measure={series[0]!} locale={locale} />
    else if (!two && (result.view === 'bar' || result.view === 'topn' || result.view === 'stacked')) chart = <MultiBars result={result} locale={locale} onPick={pick} />
  }

  return (
    <div className="od-rep-result">
      {chart}
      {result.view === 'pivot' && chart ? null : <Table result={result} locale={locale} onPick={pick} />}
      {pick ? <div className="od-rep-hint od-rep-drill-hint">{locale === 'ro' ? 'Apasa pe o bara sau pe un rand ca sa vezi inregistrarile din spatele lui.' : 'Click a bar or a row to see the records behind it.'}</div> : null}
    </div>
  )
}

function Kpis({ result, locale, gauge }: { result: RunResult; locale: Locale; gauge: boolean }) {
  const measures = result.columns.filter((c) => c.role === 'measure')
  const display = result.display
  const target = typeof display?.target === 'number' ? display.target : null
  const better = display?.better ?? 'up'
  return (
    <div className="od-rep-kpis">
      {measures.map((m, k) => {
        const i = result.columns.indexOf(m)
        const value = result.rows[0]!.values[i] ?? null
        const delta = result.compare ? change(typeof value === 'number' ? value : null, result.compare.values[i] ?? null) : null
        const rule = ruleColor(display, m.key, value)
        const goal = k === 0 && target !== null ? target : null
        const reached = goal !== null && typeof value === 'number' && goal !== 0 ? Math.round((value / goal) * 1000) / 10 : null
        const good = goal !== null && typeof value === 'number' ? (better === 'down' ? value <= goal : value >= goal) : null
        return (
          <div key={m.key} className={`od-rep-kpi${rule ? ` od-rep-rule-${rule}` : ''}`}>
            {gauge && goal !== null && typeof value === 'number' ? <Gauge value={value} target={goal} better={better} column={m} locale={locale} /> : null}
            <div className="od-rep-kpi-value" style={rule ? { color: RULE_FILL[rule]!.fg } : undefined}>
              {cellText(value, m, locale) || '0'}
            </div>
            <div className="od-rep-kpi-label">{columnTitle(m, locale)}</div>
            {delta ? (
              <div className={`od-rep-kpi-delta od-rep-kpi-${delta.arrow === '▲' ? 'up' : delta.arrow === '▼' ? 'down' : 'flat'}`} title={locale === 'ro' ? 'fata de perioada dinainte' : 'vs the previous period'}>
                {delta.arrow} {delta.pct === null ? '' : `${delta.pct > 0 ? '+' : ''}${delta.pct}%`}{' '}
                <span className="od-rep-kpi-before">
                  ({locale === 'ro' ? 'inainte' : 'before'}: {cellText(result.compare!.values[i] ?? null, m, locale) || '0'})
                </span>
              </div>
            ) : null}
            {goal !== null ? (
              <div className={`od-rep-kpi-target ${good === null ? '' : good ? 'od-rep-kpi-up' : 'od-rep-kpi-down'}`}>
                {locale === 'ro' ? 'Tinta' : 'Target'}: {cellText(goal, m, locale) || '0'}
                {reached !== null ? ` · ${reached}%` : ''}
                {!gauge && reached !== null ? (
                  <span className="od-rep-kpi-meter" aria-hidden="true">
                    <span style={{ width: `${Math.max(0, Math.min(100, reached))}%` }} className={good ? 'od-rep-meter-good' : 'od-rep-meter-bad'} />
                  </span>
                ) : null}
              </div>
            ) : gauge && k === 0 ? (
              <div className="od-rep-hint">{locale === 'ro' ? 'Pune o tinta la pasul 5 ca sa vezi indicatorul.' : 'Set a target in step 5 to see the gauge.'}</div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/** Inel: felii pe primele 7 valori, restul strans in "altele". */
function Donut({ result, group, measure, locale }: { result: RunResult; group: ResultColumn; measure: ResultColumn; locale: Locale }) {
  const gi = result.columns.indexOf(group)
  const mi = result.columns.indexOf(measure)
  const all = result.rows.map((r) => ({ label: cellText(r.values[gi] ?? null, group, locale) || (locale === 'ro' ? '(gol)' : '(empty)'), value: Math.max(0, Number(r.values[mi] ?? 0)) }))
  const head = all.slice(0, 7)
  const rest = all.slice(7).reduce((s, x) => s + x.value, 0)
  const slices = rest > 0 ? [...head, { label: locale === 'ro' ? 'altele' : 'other', value: rest }] : head
  const total = slices.reduce((s, x) => s + x.value, 0)
  if (total <= 0) return null
  const r = 70
  const c = 2 * Math.PI * r
  let offset = 0
  return (
    <div className="od-rep-donut">
      <svg width="180" height="180" viewBox="0 0 180 180" role="img" aria-label={columnTitle(measure, locale)}>
        <g transform="rotate(-90 90 90)">
          {slices.map((s, i) => {
            const len = (s.value / total) * c
            const el = <circle key={i} cx="90" cy="90" r={r} fill="none" stroke={DONUT_COLORS[i % DONUT_COLORS.length]} strokeWidth="26" strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} />
            offset += len
            return el
          })}
        </g>
        <text x="90" y="96" textAnchor="middle" className="od-rep-donut-total">
          {cellText(total, measure, locale)}
        </text>
      </svg>
      <ul className="od-rep-legend">
        {slices.map((s, i) => (
          <li key={i}>
            <span className="od-rep-swatch" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
            {s.label} <b>{cellText(s.value, measure, locale)}</b> <span className="od-rep-meta">{Math.round((s.value / total) * 1000) / 10}%</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Table({ result, locale, onPick }: { result: RunResult; locale: Locale; onPick?: (row: number) => void }) {
  const label = result.columns.findIndex((c) => c.role === 'group')
  return (
    <div className="od-rep-table-wrap">
      <table className="table table-condensed od-rep-table">
        <thead>
          <tr>
            {result.columns.map((c) => (
              <th key={c.key} className={c.role === 'measure' ? 'od-rep-num' : undefined}>
                {columnTitle(c, locale)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((r, i) => (
            <tr key={i} className={onPick ? 'od-rep-pick' : undefined} onClick={onPick ? () => onPick(i) : undefined}>
              {r.values.map((v, j) => {
                const col = result.columns[j]!
                const rule = ruleColor(result.display, col.key, v)
                const cls = `${col.role === 'measure' ? 'od-rep-num' : ''}${rule ? ` od-rep-rule-${rule}` : ''}`.trim()
                return (
                  <td key={j} className={cls || undefined}>
                    {cellText(v, col, locale)}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
        {result.foot && result.foot.length > 0 ? (
          <tfoot>
            {result.foot.map((f) => (
              <tr key={f.kind} className="od-rep-foot">
                {f.values.map((v, j) => (
                  <td key={j} className={result.columns[j]!.role === 'measure' ? 'od-rep-num' : undefined}>
                    {j === label ? FOOT_LABELS[f.kind][locale] : cellText(v, result.columns[j]!, locale)}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        ) : null}
      </table>
    </div>
  )
}
